inherit("OrderProtection", {
	parent: `Class`,
	singleton: true,
	requires: [`DOM`, `Events`, `Locale`, `ShoppingCart`, `Time`],
	prepare(setup) {
		const { data } = this;

		data.sessionStarted = false;
	},
	constructor(setup) {
		// NOTE: Class doesn't include automatic this.elements
		this.elements = { widget: null };
	},
	api: {
		createWidget: {
			async value(options) {
				const { Events, Locale, ShoppingCart, Time, data, elements } = this;
				const { wrapper } = options;
				let initialized = false;

				// this element is used to render the 3rd party widget
				const widgetElement = document.createElement(`div`);
				widgetElement.id = `tg-placeholder`;
				wrapper.appendChild(widgetElement);
				elements.widget = widgetElement;

				const tg = await this.getApi();

				const localeInfo = await Locale.get();
				const { selected: selectedLocale } = localeInfo;
				const { protechtKey: locale } = selectedLocale;

				const cart = await ShoppingCart.get();
				const { currency, eventId, orderProtectionConfiguration } = cart;
				const tixrEvent = await Events.get({ id: eventId });
				const { name: eventName, startDate, venue } = tixrEvent;
				const { timezone } = venue;
				const dateTemplate = `%Y-%N-%D`;
				const timeTemplate = `%J:%i:%S`;
				const eventStartDate = Time.format(dateTemplate, startDate, timezone);
				const eventStartTime = Time.format(timeTemplate, startDate, timezone);
				const itemEvent = { name: eventName, startDate: eventStartDate, startTime: eventStartTime };
				let totalCost;
				let items;

				await ShoppingCart.get({
					subscribe: true,
					then: (result) => {
						const { checkoutType, saleItems, upgradeOrderProtectionItemCosts } = result;
						const updatedItems = [];

						totalCost = 0;
						saleItems.forEach((saleItem) => {
							const {
								holdId,
								price,
								priceLevelOptionId,
								priceListId,
								quantity,
								saleId,
								tierId,
								totalAfterAdjustments,
							} = saleItem;
							const idString = `${saleId}`;
							const tierString = `${tierId}`;
							const holdString = this.isInt(holdId) ? `${holdId}` : ``;
							const priceLevelString = this.isInt(priceLevelOptionId) ? `${priceLevelOptionId}` : ``;
							const priceListIdString = this.isInt(priceListId) ? `${priceListId}` : ``;
							const referenceNumber = `s${idString}::t${tierString}::h${holdString}::p${priceLevelString}::l${priceListIdString}`;
							let itemCostList;

							if (checkoutType === `UPGRADE`) {
								// upgrades provides its own price mapping
								itemCostList = upgradeOrderProtectionItemCosts[saleId];
							} else {
								// calculate the right price and generate a list of it
								const itemCost = this.isFinite(totalAfterAdjustments) ? totalAfterAdjustments : price;
								itemCostList = [];

								for (let i = 0; i < quantity; i++) {
									itemCostList.push(itemCost);
								}
							}

							for (let i = 0; i < quantity; i++) {
								const event = this.clone(itemEvent);
								const itemCost = itemCostList[i];
								const cost = `${itemCost}`;

								updatedItems.push({ cost, event, referenceNumber });
								totalCost += itemCost;
							}
						});

						if (
							initialized && // widget already exists
							!this.isEqual(updatedItems, items) && // calculated items changed
							checkoutType === `PURCHASE` // costs can't change mid-upgrade
						) {
							if (totalCost > 0) {
								// widget already exists and costs changed, update the widget
								const updateOptions = { items: updatedItems, updatedCb: this.onQuoteUpdate };
								tg(`update`, updateOptions);
							} else {
								// nothing to protect, disable the widget
								tg.clear();
							}
						}

						items = updatedItems;
						return result; // pass through the cart
					},
				});
				const { accessKey: apiKey } = orderProtectionConfiguration;
				const sandbox = apiKey.includes(`sandbox`);
				const clearSelection = false;
				const persist = true;

				initialized = true;

				if (totalCost > 0) {
					return new Promise((resolve, reject) => {
						tg(`configure`, {
							apiKey,
							clearSelection,
							items,
							currency,
							locale,
							persist,
							sandbox,
							loadedCb: (result) => resolve(result),
							optInCb: () => this.onWidgetOptIn(),
							optOutCb: () => this.onWidgetOptOut(),
							onErrorCb: (error) => {
								reject(error);
								ShoppingCart.disableOrderProtection();
							},
						});

						data.sessionStarted = true;
					});
				} else {
					this.log({
						type: `warning`,
						message: `No items to protect, disabling Protecht widget`,
					});
				}
			},
		},
		destroyWidget: {
			async: true,
			async value(context) {
				const { DOM, ShoppingCart, elements } = this;
				const { widget } = elements;
				const tg = await this.getApi();

				DOM.remove(widget); // remove the widget from the DOM
				ShoppingCart.unsubscribe(); // stop listening for cart updates
				elements.widget = null;

				try {
					tg.clear();
				} catch (error) {
					this.log({
						type: `warning`,
						message: `Error clearing Protecht widget: %O`,
						args: [error],
					});

					// swallow the error
				}
			},
		},
		placeWidget: {
			async: true,
			public: true,
			debounce: 200,
			immediate: false,
			async value(context, options) {
				const { DOM, elements } = this;
				const { widget } = elements;
				const { wrapper } = options;

				if (this.isElement(widget)) {
					// widget exists

					if (!DOM.containsElement(widget, wrapper)) {
						await this.destroyWidget(); // remove the widget from the old location
						return this.createWidget({ wrapper });
					}
				} else {
					// widget does not exist, create
					return this.createWidget({ wrapper });
				}
			},
		},
		getApi: {
			async: true,
			async value() {
				// Based on init code from https://docs.protecht.io/docs/rendering-the-widget#tg-configure-command
				if (window.tg === undefined) {
					try {
						// adapt their embed snippet
						window.tg = function () {
							window.tg.q.push(arguments);
						};
						window.tg.q = [];
						window.tg.l = Date.now();

						// load the JS library
						const deps = [`https://icw.protecht.io/client-widget.min.js`];
						await this.require({ deps });
					} catch (error) {
						window.tg = undefined;

						throw error;
					}
				}
				return window.tg;
			},
		},
		onQuoteUpdate: {
			async value() {
				const tg = await this.getApi();
				const isProtected = tg.isProtected();

				// new quote was fetched
				if (isProtected) {
					// order is opted in
					this.onWidgetOptIn(); // notify the server of the new quote
				}
			},
		},
		onWidgetOptIn: {
			async value() {
				const { ShoppingCart } = this;
				const cart = await ShoppingCart.get();

				if (this.isString(cart.token, 1)) {
					const tg = await this.getApi();
					const token = tg.get(`token`);
					const quoteString = tg.get(`quote`);
					const quote = Number.parseFloat(quoteString);
					const protectionOptions = { quote, token };

					ShoppingCart.addOrderProtection(protectionOptions);
				}
			},
		},
		onWidgetOptOut: {
			async value() {
				const { ShoppingCart } = this;
				const cart = await ShoppingCart.get();

				if (this.isString(cart.token, 1)) {
					ShoppingCart.removeOrderProtection();
				}
			},
		},

		clearSession: {
			async: true,
			public: true,
			async value(context) {
				const { data } = this;
				const { sessionStarted } = data;

				if (sessionStarted) {
					data.sessionStarted = false;

					const tg = await this.getApi();
					tg.removePersistedSession();
				}
			},
		},

		onDie: {
			public: true,
			value(context) {
				const { DOM, elements } = this;
				const { container } = context;
				const { widget } = elements;

				if (this.isElement(widget) && DOM.containsElement(widget, container)) {
					// widget exists and is in the dying instance
					this.destroyWidget(); // remove the widget from the old location
				}
			},
		},
	},
});

//# sourceMappingURL=OrderProtection.js.map